import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { CircularProgress } from 'src/components/CircularProgress';
import { Icon } from 'src/components/Icon';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { isTrainer } from 'src/models/user/utils';

import { usePosContext } from '../PosContext';

export function PosTopBar() {
  const { t } = useTranslation('translation', { keyPrefix: 'pos.topBar' });
  const { user, session } = useOptionalSession();
  const {
    selectedTerminal,
    setShouldShowTerminalDialog,
    isReaderConnecting,
    readerConnectionError,
    retryReaderConnection,
  } = usePosContext();
  const navigate = useNavigate();

  const handleReaderClick = () => {
    setShouldShowTerminalDialog(true);
  };

  const handleConfigureClick = () => {
    navigate('/pos-config/settings');
  };

  const isReaderConnected = selectedTerminal?.status === 'online';

  return (
    <Box
      sx={{
        backgroundColor: 'black',
        color: 'white',
        px: 2,
        py: 0,
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        {/* Left side - Reader status and name */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ cursor: 'pointer' }}
          onClick={handleReaderClick}
        >
          <Box
            component="img"
            p={2}
            src={session?.darkmodeLogoUrl || session?.logoUrl}
            alt="Logo"
            sx={{
              maxHeight: '56px',
              maxWidth: '56px',
            }}
          />

          {/* Reader status with checkmark */}
          <Stack direction="row" alignItems="center" spacing={1}>
            {isReaderConnecting ? (
              <CircularProgress size="small" color="inherit" />
            ) : isReaderConnected ? (
              <Icon icon={faCheck} color="success" size="xsmall" />
            ) : (
              <Icon icon={faTimes} color="error" size="xsmall" />
            )}
            <Typography variant="body2" fontWeight={500}>
              {t('reader')}
            </Typography>
          </Stack>

          {/* Device name */}
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {selectedTerminal?.label || t('noReaderConnected')}
          </Typography>

          {/* Connection error or retry button */}
          {readerConnectionError && !isReaderConnecting && (
            <Button
              variant="borderless"
              color="inherit"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                retryReaderConnection();
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                minWidth: 'auto',
                p: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {t('retry')}
            </Button>
          )}
        </Stack>

        {/* Right side - Configure link */}
        {user && isTrainer(user) && (
          <Button
            variant="borderless"
            color="inherit"
            onClick={handleConfigureClick}
            startIcon={<Icon icon={faExternalLink} size="tiny" />}
            sx={{
              '& .MuiButton-startIcon': {
                fontSize: '14px',
              },
              '& .MuiButton-startIcon > *': {
                fontSize: '14px !important',
              },
            }}
          >
            {t('configure')}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
